'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';

// Mock data extending PopularCities data with states
const allCitiesData = [
    { id: 1, name: 'Chennai', state: 'Tamil Nadu', slug: 'chennai', propertyCount: 12500, image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=600' },
    { id: 2, name: 'Bangalore', state: 'Karnataka', slug: 'bangalore', propertyCount: 18200, image: 'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=600' },
    { id: 3, name: 'Hyderabad', state: 'Telangana', slug: 'hyderabad', propertyCount: 9800, image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=600' },
    { id: 4, name: 'Mumbai', state: 'Maharashtra', slug: 'mumbai', propertyCount: 22000, image: 'https://images.unsplash.com/photo-1567157577867-05ccb1388e66?w=600' },
    { id: 5, name: 'Delhi', state: 'Delhi', slug: 'delhi', propertyCount: 15600, image: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=600' },
    { id: 6, name: 'Pune', state: 'Maharashtra', slug: 'pune', propertyCount: 8400, image: 'https://images.unsplash.com/photo-1567157577867-05ccb1388e66?w=600' },
    { id: 7, name: 'Kochi', state: 'Kerala', slug: 'kochi', propertyCount: 5200, image: 'https://images.unsplash.com/photo-1596176530529-78163a4f7af2?w=600' },
    { id: 8, name: 'Visakhapatnam', state: 'Andhra Pradesh', slug: 'visakhapatnam', propertyCount: 3100, image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=600' },
    { id: 9, name: 'Vijayawada', state: 'Andhra Pradesh', slug: 'vijayawada', propertyCount: 2200, image: 'https://images.unsplash.com/photo-1567157577867-05ccb1388e66?w=600' },
    { id: 10, name: 'Thiruvananthapuram', state: 'Kerala', slug: 'thiruvananthapuram', propertyCount: 1800, image: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=600' },
    { id: 11, name: 'Kozhikode', state: 'Kerala', slug: 'kozhikode', propertyCount: 1500, image: 'https://images.unsplash.com/photo-1582510003544-4d00b7f74220?w=600' },
    { id: 12, name: 'Mysore', state: 'Karnataka', slug: 'mysore', propertyCount: 2500, image: 'https://images.unsplash.com/photo-1567157577867-05ccb1388e66?w=600' },
    { id: 13, name: 'Coimbatore', state: 'Tamil Nadu', slug: 'coimbatore', propertyCount: 4500, image: 'https://images.unsplash.com/photo-1587474260584-136574528ed5?w=600' }
];

// Extract distinct states
const states = [...new Set(allCitiesData.map(city => city.state))].sort();

export default function AllCitiesPage() {
    const [selectedState, setSelectedState] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const filteredCities = useMemo(() => {
        return allCitiesData.filter(city => {
            const matchesState = selectedState === '' || city.state === selectedState;
            const matchesSearch = city.name.toLowerCase().includes(searchQuery.toLowerCase());
            return matchesState && matchesSearch;
        });
    }, [selectedState, searchQuery]);

    return (
        <section className="py-8 lg:py-6 bg-gray-50 min-h-screen">
            <div className="container-custom">
                {/* Top Header */}
                <div className="mb-8">
                    <div className="section-title">
                        <h2>Explore All Cities</h2>
                        <p>Find your perfect property across top cities and states in India</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white p-4 lg:p-6 rounded-xl shadow-sm border border-gray-100 mb-8">
                    <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                        {/* Left Side: State Select Filter */}
                        <div className="w-full md:w-72">
                            <label htmlFor="stateFilter" className="block text-sm font-medium text-gray-700 mb-1">Filter by State</label>
                            <select
                                id="stateFilter"
                                value={selectedState}
                                onChange={(e) => setSelectedState(e.target.value)}
                                className="input select w-full"
                            >
                                <option value="">All States</option>
                                {states.map(state => (
                                    <option key={state} value={state}>{state}</option>
                                ))}
                            </select>
                        </div>

                        {/* Right Side: Search Box */}
                        <div className="w-full md:w-96">
                            <label htmlFor="citySearch" className="block text-sm font-medium text-gray-700 mb-1">Search City</label>
                            <div className="input-group">
                                <span className="input-icon">
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </span>
                                <input
                                    id="citySearch"
                                    type="text"
                                    placeholder="Search by city name..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="input input-with-icon"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Results Info */}
                <div className="mb-6 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">
                        Cities ({filteredCities.length})
                    </h3>
                    {(selectedState || searchQuery) && (
                        <button
                            className="text-sm text-primary hover:underline"
                            onClick={() => {
                                setSelectedState('');
                                setSearchQuery('');
                            }}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>

                {/* Cities Grid (Reused styles from PopularCities) */}
                {filteredCities.length > 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6">
                        {filteredCities.map((city) => (
                            <Link
                                key={city.id}
                                href={`/city/${city.slug}`}
                                className="city-card group block"
                            >
                                <img
                                    src={city.image}
                                    alt={city.name}
                                    loading="lazy"
                                />
                                <div className="overlay">
                                    <span className="text-xs uppercase tracking-wider text-white/80 font-medium mb-1 drop-shadow-md">
                                        {city.state}
                                    </span>
                                    <h3 className="transform translate-y-1 group-hover:translate-y-0 transition-transform">{city.name}</h3>
                                    <p className="opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                                        {city.propertyCount.toLocaleString()}+ properties
                                    </p>
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center flex flex-col items-center justify-center">
                        <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">No cities found</h3>
                        <p className="text-gray-500 max-w-md mx-auto">
                            We couldn't find any cities matching your current filters. Try adjusting your search query or selecting a different state.
                        </p>
                        <button
                            className="btn btn-secondary mt-6"
                            onClick={() => {
                                setSelectedState('');
                                setSearchQuery('');
                            }}
                        >
                            Reset Filters
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}
